import { useMemo } from 'react';

import { TFormalOffers } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersMaxInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOffersMaxInterestOnlyPeriod';
import { getHasRCFOffer } from '@helpers/FormalOffers/getHasRCFOffer';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getIsRBLOffer } from '@helpers/FormalOffers/getIsRBLOffer';
import { getDurationInYears } from '@helpers/GetDurationInYears/getDurationInYears';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../../iwQueryClient/useStateKey';

export const useGetSelectOfferTypeData = () => {
    const stateKey = useStateKey();

    const { data: formalOffersResponse } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        suspense: true
    });

    if (!formalOffersResponse) {
        throw new Error();
    }
    const formalOffers = formalOffersResponse.data.formal_offers;

    // This needs to be memoized due to it being a dependency of flexibleOffers
    // Removing this memoiziation will cause an infinite loop since Array.filter generates a new array each time
    // See - https://git.iwoca.co.uk/iwoca/pandora/-/merge_requests/5649#note_1658851
    const rblOffers = useMemo(
        () => formalOffers.filter((offer) => getIsRBLOffer(offer)),
        [formalOffers]
    );

    // This needs to be memoized due to it being a dependency in a useEffect in <SelectOfferTypeButtons />
    // Removing this memoiziation will cause an infinite loop since Array.filter generates a new array each time
    // See - https://git.iwoca.co.uk/iwoca/pandora/-/merge_requests/5649#note_1658851
    const flexibleOffers = useMemo(() => {
        if (rblOffers.length > 0) {
            return formalOffers.filter(
                (offer) =>
                    !getIsInterestOnlyOffer(offer) && !getIsRBLOffer(offer)
            );
        } else {
            return formalOffers.filter(
                (offer) =>
                    offer.duration!.amount! <= 730 &&
                    !getIsInterestOnlyOffer(offer) &&
                    !getIsRBLOffer(offer)
            );
        }
    }, [formalOffers, rblOffers]);

    const longerDurationOffers = formalOffers.filter(
        (offer) => offer.duration!.amount! > 730
    );

    const interestOnlyOffers = formalOffers.filter((offer) =>
        getIsInterestOnlyOffer(offer)
    );

    return {
        rblOffers,
        flexibleOffers,
        longerDurationOffers,
        interestOnlyOffers,
        maxFlexibleLoanYear: getMaxFlexibleLoanYear(flexibleOffers),
        shouldUseInterestOnlyPlusCopy:
            getFormalOffersMaxInterestOnlyPeriod(interestOnlyOffers) >= 6,
        hasRCFOffer: getHasRCFOffer(formalOffers)
    };
};

const getMaxFlexibleLoanYear = (flexibleOffers: TFormalOffers) => {
    const maxDuration = flexibleOffers.reduce((max, current) =>
        current!.duration!.amount > max.duration!.amount ? current : max
    );
    const durationInYears = getDurationInYears(maxDuration.duration!);

    return durationInYears;
};
